import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { StateBlock, StoreCard } from "../components/Cards";
import { useAuth } from "../lib/auth";
import { buyStoreItem, createStoreItem, deleteStoreItem, getStoreItems, getUser, updateStoreItem } from "../lib/data";
import { setSeo } from "../lib/seo";
import { useAsync } from "../lib/useAsync";
import type { StoreItem } from "../lib/types";

export function Store() {
  const { user } = useAuth();
  const [notice, setNotice] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<StoreItem | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, loading, error } = useAsync(getStoreItems, [refreshKey]);
  const { data: profile, loading: profileLoading, error: profileError } = useAsync(
    () => (user ? getUser(user.uid) : Promise.resolve(null)),
    [user?.uid, notice]
  );

  useEffect(() => {
    setSeo({
      title: "Forum Store | My Local Forum",
      description: "Browse available profile items in the My Local Forum store.",
      path: "/store"
    });
  }, []);

  async function buy(item: StoreItem) {
    if (!profile) return;
    setNotice(null);
    try {
      await buyStoreItem(profile, item);
      setNotice(`Purchased ${item.name}.`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Purchase failed.");
    }
  }

  async function saveStoreItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") || "").trim();
    const description = String(form.get("description") || "").trim();
    const price = Number(String(form.get("price") || "0"));
    const imageUrl = String(form.get("imageUrl") || "").trim();

    if (!name || Number.isNaN(price) || price < 0) {
      setNotice("Name and a valid price are required.");
      return;
    }

    try {
      if (editingItem) {
        await updateStoreItem(editingItem.id, { name, description, price, imageUrl });
        setNotice("Store item updated.");
      } else {
        await createStoreItem({ name, description, price, imageUrl });
        setNotice("Store item created.");
      }
      setEditingItem(null);
      event.currentTarget.reset();
      setRefreshKey((key) => key + 1);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to save store item.");
    }
  }

  async function removeStoreItem(item: StoreItem) {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    setNotice(null);
    try {
      await deleteStoreItem(item.id);
      setNotice("Store item deleted.");
      if (editingItem?.id === item.id) setEditingItem(null);
      setRefreshKey((key) => key + 1);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to delete store item.");
    }
  }

  const purchasedItems = new Set(profile?.purchasedItems || []);
  const coins = profile?.coins || 0;
  const isAdmin = profile?.role?.toUpperCase() === "ADMIN";

  return (
    <section className="page">
      <h1>Forum Store</h1>
      {!user ? <Link className="primary-button" to="/auth">Sign in to buy store items</Link> : null}
      {user ? <div className="panel store-wallet">Your Coins: {profileLoading ? "Loading..." : coins}</div> : null}
      {notice ? <div className="panel store-notice">{notice}</div> : null}
      {isAdmin ? (
        <form className="panel form-grid store-admin-form" onSubmit={saveStoreItem}>
          <h2>{editingItem ? "Edit Store Item" : "Add Store Item"}</h2>
          <input name="name" placeholder="Item name" defaultValue={editingItem?.name || ""} key={`name-${editingItem?.id || "new"}`} />
          <textarea
            name="description"
            placeholder="Description"
            defaultValue={editingItem?.description || ""}
            key={`description-${editingItem?.id || "new"}`}
          />
          <input
            name="price"
            placeholder="Price"
            type="number"
            min="0"
            step="1"
            defaultValue={editingItem?.price ?? 0}
            key={`price-${editingItem?.id || "new"}`}
          />
          <input
            name="imageUrl"
            placeholder="Image URL"
            defaultValue={editingItem?.imageUrl || ""}
            key={`image-${editingItem?.id || "new"}`}
          />
          <div className="admin-inline-actions">
            <button className="primary-button" type="submit">
              {editingItem ? "Update Item" : "Add Item"}
            </button>
            {editingItem ? (
              <button className="ghost-button" type="button" onClick={() => setEditingItem(null)}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      ) : null}
      <StateBlock loading={loading} error={error || profileError} empty={data?.length === 0} />
      <div className="masonry">
        {data?.map((item) => (
          <StoreCard
            key={item.id}
            item={item}
            owned={purchasedItems.has(item.id)}
            canAfford={coins >= (item.price || 0)}
            disabled={!user || !profile || profileLoading}
            isAdmin={isAdmin}
            onBuy={buy}
            onEdit={setEditingItem}
            onDelete={removeStoreItem}
          />
        ))}
      </div>
    </section>
  );
}
